/**
 * TotalsCard — Récap totaux étendus (Sprint 89 — parité Axonaut).
 *
 * Affiche dans l'ordre :
 *   Sous-total HT
 *   − Remise globale (si > 0)
 *   + Frais annexes HT (si > 0)
 *   TVA par taux (ventilation)
 *   Total TTC
 *   − Retenue de garantie (si > 0, BTP)
 *   − Acompte encaissé (si > 0)
 *   ⇒ Solde à payer (si retenue ou acompte)
 */
import { Card } from "@/components/ui/card";
import { motion } from "framer-motion";

const fmtEuro = (n) =>
  `${(Number(n) || 0).toLocaleString("fr-FR", {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })} €`;

const Row = ({ label, value, dim, bold, danger, testid, indent, highlight }) => (
  <div className={`flex justify-between ${bold ? "text-sm" : "text-xs"}`}>
    <span className={
      highlight ? "text-blue-700 font-semibold" :
      danger    ? "text-red-600" :
      bold      ? "text-slate-900 font-semibold" :
      dim       ? "text-slate-500" : "text-slate-600"
    } style={indent ? { paddingLeft: indent } : undefined}>
      {label}
    </span>
    <span className={`font-mono tabular-nums ${
      highlight ? "text-blue-700 font-semibold text-base" :
      danger    ? "text-red-600" :
      bold      ? "text-slate-900 font-semibold text-lg" :
      "text-slate-700"
    }`} data-testid={testid}>
      {fmtEuro(value)}
    </span>
  </div>
);

export default function TotalsCard({ totals, docKind }) {
  const t = totals || {};
  const disc = Number(t.global_discount_amount || 0);
  const ship = Number(t.shipping_ht || 0);
  const retenue = Number(t.retenue_garantie_amount || 0);
  const deposit = Number(t.deposit_amount || 0);
  const balance = Number(t.balance_due ?? t.total_ttc ?? 0);
  const hasDeductions = retenue > 0 || deposit > 0;
  const isProforma = docKind === "proforma";

  return (
    <motion.div initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }}>
      <Card className="border-slate-200 bg-white rounded-xl p-5 space-y-2.5"
             data-testid="totals-card">
        <div className="flex items-center justify-between">
          <div className="text-[10px] font-medium uppercase tracking-widest text-slate-500">
            Totaux
          </div>
          {isProforma && (
            <span className="text-[9px] uppercase font-bold text-violet-700 bg-violet-100 px-1.5 py-0.5 rounded">
              Proforma
            </span>
          )}
        </div>

        <Row label="Sous-total HT" value={t.subtotal_ht} testid="total-ht" />

        {disc > 0 && (
          <Row label="− Remise globale" value={-disc} danger testid="total-discount" />
        )}
        {ship > 0 && (
          <Row label="+ Frais annexes HT" value={ship} testid="total-shipping" />
        )}

        {(t.vat_by_rate || []).map((v) => (
          <Row key={v.rate} label={`TVA ${v.rate}%`} value={v.vat_amount} dim />
        ))}

        <div className="border-t border-slate-200 pt-2.5">
          <Row label="Total TTC" value={t.total_ttc} bold testid="total-ttc" />
        </div>

        {retenue > 0 && (
          <Row label="− Retenue de garantie" value={-retenue} danger testid="total-retenue" />
        )}
        {deposit > 0 && (
          <Row label="− Acompte encaissé" value={-deposit} danger testid="total-deposit" />
        )}

        {hasDeductions && (
          <div className="border-t border-blue-200 pt-2.5 mt-2">
            <Row label="Solde à payer" value={balance} highlight testid="total-balance-due" />
          </div>
        )}

        {Number(t.situation_previous_ht || 0) > 0 && (
          <div className="mt-2 pt-2 border-t border-emerald-200 text-[10px] text-emerald-700 italic">
            Situation : {fmtEuro(t.situation_previous_ht)} HT déjà facturés antérieurement
          </div>
        )}
      </Card>
    </motion.div>
  );
}
